
// =============================================
// FIXED PDF EXPORT UTILITY
// utils/pdfExport.js - Now matches on-screen report data exactly
// =============================================

import { jsPDF } from 'jspdf'
import autoTable from 'jspdf-autotable'
import { processTimeEntries, calculateSummaryWithSplits } from './timeSplitUtils'

// Helper function to convert hex to RGB
const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : { r: 59, g: 130, b: 246 } // Default blue
}

// Helper function to format date ranges
const formatDateRange = (filters, settings) => {
  if (!filters.startDate && !filters.endDate) return null
  
  const startDate = filters.startDate ? new Date(filters.startDate) : null
  const endDate = filters.endDate ? new Date(filters.endDate) : null
  
  const formatDate = (date) => {
    if (!date) return 'Present'
    
    switch (settings.dateRange?.format || 'full') {
      case 'short':
        return date.toLocaleDateString('en-US', { 
          month: '2-digit', 
          day: '2-digit', 
          year: '2-digit' 
        })
      case 'iso':
        return date.toISOString().split('T')[0]
      case 'custom':
        return date.toLocaleDateString('en-US')
      case 'full':
      default:
        return date.toLocaleDateString('en-US', { 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric' 
        })
    }
  }

  if (startDate && endDate) {
    return `${formatDate(startDate)} - ${formatDate(endDate)}`
  } else if (startDate) {
    return `From ${formatDate(startDate)}`
  } else if (endDate) {
    return `Until ${formatDate(endDate)}`
  }
  return null
}

export const generatePDFReport = async (reportConfig, exportSettings) => {
  const { reportType, data, filters, tenant } = reportConfig
  const { template, branding, confidentiality, dateRange, customSections } = exportSettings

  try {
    const pdf = new jsPDF()
    const pageWidth = pdf.internal.pageSize.getWidth()
    const pageHeight = pdf.internal.pageSize.getHeight()
    
    // Set colors from branding
    const primaryColor = hexToRgb(branding.primaryColor)
    const accentColor = hexToRgb(branding.accentColor)
    
    let yPosition = 15 // Start higher on page

    // Add watermark if enabled
    if (confidentiality?.enabled && confidentiality?.showWatermark) {
      addWatermark(pdf, confidentiality.level, pageWidth, pageHeight, primaryColor)
    }

    // Header based on template
    yPosition = await addTemplatedHeader(pdf, template, branding, yPosition, primaryColor, accentColor)

    // Report title and metadata (more compact)
    yPosition = addReportTitle(pdf, reportType, filters, exportSettings, yPosition, primaryColor)

    // Date range display (compact)
    if (dateRange?.showOnCover) {
      const dateRangeText = formatDateRange(filters, exportSettings)
      if (dateRangeText) {
        yPosition = addDateRange(pdf, dateRangeText, yPosition)
      }
    }

    // Filters summary (inline, compact)
    yPosition = addFiltersSection(pdf, filters, yPosition)

    // Small buffer before content
    yPosition += 6

    // Executive summary for executive template
    if (template === 'executive') {
      yPosition = await addExecutiveSummary(pdf, data, yPosition, primaryColor, accentColor)
    }

    // FIXED: Main report content - now processes data same way as screen components
    yPosition = await addTemplatedContent(pdf, template, reportType, data, yPosition, primaryColor, accentColor)

    // Add custom sections
    if (customSections && customSections.length > 0) {
      for (const section of customSections) {
        yPosition = addCustomSection(pdf, section, yPosition, primaryColor)
      }
    }

    // FIXED: Add footer to ALL pages
    addFooterToAllPages(pdf, confidentiality, branding)

    // Save the PDF
    const filename = generateFileName(reportType, filters)
    pdf.save(filename)

  } catch (error) {
    console.error('Error generating PDF:', error)
    throw new Error('Failed to generate PDF report')
  }
}

// FIXED: Main content function now matches screen display exactly
const addTemplatedContent = async (pdf, template, reportType, data, yPosition, primaryColor, accentColor) => {
  // Check for page break
  if (yPosition > 200) {
    pdf.addPage()
    yPosition = 20
  }

  // Process data using same split logic as screen components
  const processedData = processTimeEntries(data)
  
  switch (reportType) {
    case 'summary':
      return addEnhancedSummaryContent(pdf, processedData, data, yPosition, template, primaryColor, accentColor)
    case 'detailed':
      return addEnhancedDetailedContent(pdf, processedData, yPosition, template, primaryColor, accentColor)
    case 'clients':
      return addEnhancedClientContent(pdf, processedData, yPosition, template, primaryColor, accentColor)
    case 'projects':
      return addEnhancedProjectContent(pdf, processedData, yPosition, template, primaryColor, accentColor)
    case 'users':
      return addEnhancedUserContent(pdf, processedData, yPosition, template, primaryColor, accentColor)
    default:
      return addEnhancedSummaryContent(pdf, processedData, data, yPosition, template, primaryColor, accentColor)
  }
}

// FIXED: Summary report now matches screen version exactly
const addEnhancedSummaryContent = (pdf, processedData, originalData, yPosition, template, primaryColor, accentColor) => {
  // Use same calculation as SummaryReport component
  const summary = calculateSummaryWithSplits(originalData)
  
  const tableStyle = getTableStyle(template, primaryColor, accentColor)
  
  // FIXED: Header now says "Revenue Generated" not "Cost" to match screen
  const summaryData = [
    ['Metric', 'Value', 'Details'],
    ['Total Hours', `${summary.totalHours.toFixed(1)}h`, `Across ${summary.totalEntries} entries`],
    ['Billable Hours', `${summary.billableHours.toFixed(1)}h`, `${summary.totalHours > 0 ? 
      ((summary.billableHours / summary.totalHours) * 100).toFixed(1) : 0}% of total`],
    ['Revenue Generated', `$${summary.revenue.toLocaleString()}`, `Average: $${summary.billableHours > 0 ? (summary.revenue / summary.billableHours).toFixed(2) : '0.00'}/hr`],
    ['Projects Worked', summary.uniqueProjects.toString(), `Across ${summary.uniqueClients} clients`],
    ['Utilization Rate', `${summary.utilizationRate.toFixed(1)}%`, 
      summary.utilizationRate > 80 ? 'Excellent' : 
      summary.utilizationRate > 60 ? 'Good' : 'Needs Improvement']
  ]

  autoTable(pdf, {
    head: [summaryData[0]],
    body: summaryData.slice(1),
    startY: yPosition,
    ...tableStyle,
    columnStyles: {
      0: { cellWidth: 40, fontStyle: 'bold' },
      1: { cellWidth: 30, halign: 'center', fontStyle: 'bold' },
      2: { cellWidth: 70, fontSize: 8 }
    }
  })

  let currentY = pdf.lastAutoTable.finalY + 20

  // FIXED: Top projects table matches screen headers exactly
  const topProjects = getTopProjectsByHours(processedData, 5)
  
  if (topProjects.length > 0) {
    // Check for page break
    if (currentY > 200) {
      pdf.addPage()
      currentY = 20
    }

    pdf.setFontSize(12)
    pdf.setFont('helvetica', 'bold')
    pdf.setTextColor(primaryColor.r, primaryColor.g, primaryColor.b)
    pdf.text('Top 5 Projects by Hours', 20, currentY)
    currentY += 10

    // FIXED: Column headers now match SummaryReport.js exactly: Project, Client, Hours, Cost
    const projectsData = [
      ['Project', 'Client', 'Hours', 'Cost'],
      ...topProjects.map(project => [
        project.name,
        project.clientName || 'No Client',
        `${project.hours.toFixed(1)}h`,
        `$${project.revenue.toFixed(0)}`
      ])
    ]

    autoTable(pdf, {
      head: [projectsData[0]],
      body: projectsData.slice(1),
      startY: currentY,
      ...tableStyle
    })
    
    currentY = pdf.lastAutoTable.finalY + 10
  }

  return currentY
}

// FIXED: Detailed report now shows ALL columns that appear on screen
const addEnhancedDetailedContent = (pdf, processedData, yPosition, template, primaryColor, accentColor) => {
  const tableStyle = getTableStyle(template, primaryColor, accentColor)
  
  // FIXED: Now includes ALL columns that appear in DetailedReport component
  const tableHeaders = [
    'Date', 'User', 'Client', 'Project', 'Task', 
    'Hours', 'Billable', 'Rate', 'Amount', 'Status'
  ]
  
  // FIXED: Map data to match exact screen display including split entries
  const tableData = processedData.map(entry => [
    entry.entry_date ? new Date(entry.entry_date).toLocaleDateString() : 'N/A',
    entry.user_name || 'Unknown',
    entry.client_name || 'No Client',
    entry.project_name || 'No Project',
    entry.task_name || 'No Task', // FIXED: Use task_name not task_description
    `${(entry.duration_hours || 0).toFixed(2)}h`,
    entry.is_billable ? 'Yes' : 'No',
    entry.is_billable ? `$${(entry.hourly_rate || 0).toFixed(2)}` : '-',
    entry.is_billable ? `$${((entry.duration_hours || 0) * (entry.hourly_rate || 0)).toFixed(2)}` : '$0.00',
    (entry.status || 'draft').charAt(0).toUpperCase() + (entry.status || 'draft').slice(1)
  ])

  autoTable(pdf, {
    head: [tableHeaders],
    body: tableData,
    startY: yPosition,
    ...tableStyle,
    columnStyles: {
      0: { cellWidth: 20 }, // Date
      1: { cellWidth: 20 }, // User
      2: { cellWidth: 20 }, // Client
      3: { cellWidth: 25 }, // Project
      4: { cellWidth: 30 }, // Task
      5: { cellWidth: 15, halign: 'right' }, // Hours
      6: { cellWidth: 15, halign: 'center' }, // Billable
      7: { cellWidth: 18, halign: 'right' }, // Rate
      8: { cellWidth: 20, halign: 'right' }, // Amount
      9: { cellWidth: 15, halign: 'center' } // Status
    },
    styles: {
      fontSize: 8,
      cellPadding: 2
    },
    headStyles: {
      fontSize: 9,
      fontStyle: 'bold'
    }
  })

  return pdf.lastAutoTable.finalY + 20
}

// Helper function to get top projects (matches SummaryReport logic)
const getTopProjectsByHours = (data, limit = 5) => {
  const projectMap = new Map()
  
  data.forEach(entry => {
    const key = entry.project_id || 'no-project'
    const existing = projectMap.get(key) || {
      id: key,
      name: entry.project_name || 'No Project',
      clientName: entry.client_name || 'No Client',
      hours: 0,
      revenue: 0
    }
    
    existing.hours += entry.duration_hours || 0
    if (entry.is_billable) {
      existing.revenue += (entry.duration_hours || 0) * (entry.hourly_rate || 0)
    }
    
    projectMap.set(key, existing)
  })
  
  return Array.from(projectMap.values())
    .sort((a, b) => b.hours - a.hours)
    .slice(0, limit)
}

// FIXED: Client and Project reports with correct data processing
const addEnhancedClientContent = (pdf, processedData, yPosition, template, primaryColor, accentColor) => {
  const tableStyle = getTableStyle(template, primaryColor, accentColor)
  
  // Group by client like ClientReport component does
  const clientMap = new Map()
  
  processedData.forEach(entry => {
    const clientId = entry.client_id || 'no-client'
    const existing = clientMap.get(clientId) || {
      id: clientId,
      name: entry.client_name || 'No Client',
      totalHours: 0,
      billableHours: 0,
      revenue: 0,
      entries: 0
    }
    
    existing.totalHours += entry.duration_hours || 0
    existing.entries += 1
    
    if (entry.is_billable) {
      existing.billableHours += entry.duration_hours || 0
      existing.revenue += (entry.duration_hours || 0) * (entry.hourly_rate || 0)
    }
    
    clientMap.set(clientId, existing)
  })
  
  const clientData = Array.from(clientMap.values())
    .sort((a, b) => b.revenue - a.revenue)
  
  const tableHeaders = ['Client', 'Entries', 'Total Hours', 'Billable Hours', 'Revenue', 'Utilization']
  const tableData = clientData.map(client => [
    client.name,
    client.entries.toString(),
    `${client.totalHours.toFixed(1)}h`,
    `${client.billableHours.toFixed(1)}h`,
    `$${client.revenue.toLocaleString()}`,
    `${client.totalHours > 0 ? ((client.billableHours / client.totalHours) * 100).toFixed(1) : 0}%`
  ])

  autoTable(pdf, {
    head: [tableHeaders],
    body: tableData,
    startY: yPosition,
    ...tableStyle
  })

  return pdf.lastAutoTable.finalY + 20
}

const addEnhancedProjectContent = (pdf, processedData, yPosition, template, primaryColor, accentColor) => {
  const tableStyle = getTableStyle(template, primaryColor, accentColor)
  
  // Group by project like ProjectReport component does
  const projectMap = new Map()
  
  processedData.forEach(entry => {
    const projectId = entry.project_id || 'no-project'
    const existing = projectMap.get(projectId) || {
      id: projectId,
      name: entry.project_name || 'No Project',
      clientName: entry.client_name || 'No Client',
      totalHours: 0,
      revenue: 0,
      entries: 0
    }
    
    existing.totalHours += entry.duration_hours || 0
    existing.entries += 1
    
    if (entry.is_billable) {
      existing.revenue += (entry.duration_hours || 0) * (entry.hourly_rate || 0)
    }
    
    projectMap.set(projectId, existing)
  })
  
  const projectData = Array.from(projectMap.values())
    .sort((a, b) => b.totalHours - a.totalHours)
  
  const tableHeaders = ['Project', 'Client', 'Entries', 'Total Hours', 'Revenue']
  const tableData = projectData.map(project => [
    project.name,
    project.clientName,
    project.entries.toString(),
    `${project.totalHours.toFixed(1)}h`,
    `$${project.revenue.toLocaleString()}`
  ])

  autoTable(pdf, {
    head: [tableHeaders],
    body: tableData,
    startY: yPosition,
    ...tableStyle
  })

  return pdf.lastAutoTable.finalY + 20
}

const addEnhancedUserContent = (pdf, processedData, yPosition, template, primaryColor, accentColor) => {
  const tableStyle = getTableStyle(template, primaryColor, accentColor)
  
  // Group by user like UserReport component does
  const userMap = new Map()
  
  processedData.forEach(entry => {
    const userId = entry.user_id || 'no-user'
    const existing = userMap.get(userId) || {
      id: userId,
      name: entry.user_name || 'Unknown User',
      totalHours: 0,
      billableHours: 0,
      revenue: 0
    }
    
    existing.totalHours += entry.duration_hours || 0
    if (entry.is_billable) {
      existing.billableHours += entry.duration_hours || 0
      existing.revenue += (entry.duration_hours || 0) * (entry.hourly_rate || 0)
    }
    
    userMap.set(userId, existing)
  })
  
  const userData = Array.from(userMap.values())
    .sort((a, b) => b.totalHours - a.totalHours)
  
  const tableHeaders = ['Team Member', 'Total Hours', 'Billable Hours', 'Revenue', 'Utilization']
  const tableData = userData.map(user => [
    user.name,
    `${user.totalHours.toFixed(1)}h`,
    `${user.billableHours.toFixed(1)}h`,
    `$${user.revenue.toLocaleString()}`,
    `${user.totalHours > 0 ? ((user.billableHours / user.totalHours) * 100).toFixed(1) : 0}%`
  ])

  autoTable(pdf, {
    head: [tableHeaders],
    body: tableData,
    startY: yPosition,
    ...tableStyle
  })

  return pdf.lastAutoTable.finalY + 20
}

// Rest of the helper functions remain the same...
const getTableStyle = (template, primaryColor, accentColor) => {
  const baseStyle = {
    styles: {
      fontSize: 9,
      cellPadding: 3,
      halign: 'left',
      valign: 'middle'
    },
    headStyles: {
      fillColor: [primaryColor.r, primaryColor.g, primaryColor.b],
      textColor: 255,
      fontSize: 10,
      fontStyle: 'bold'
    },
    alternateRowStyles: {
      fillColor: [248, 250, 252]
    },
    margin: { left: 20, right: 20 }
  }

  switch (template) {
    case 'modern':
      return {
        ...baseStyle,
        styles: {
          ...baseStyle.styles,
          lineColor: [accentColor.r, accentColor.g, accentColor.b],
          lineWidth: 0.5
        }
      }
    case 'minimal':
      return {
        ...baseStyle,
        styles: {
          ...baseStyle.styles,
          fontSize: 8
        },
        headStyles: {
          ...baseStyle.headStyles,
          fillColor: [248, 250, 252],
          textColor: [60, 60, 60],
          fontStyle: 'normal'
        }
      }
    default:
      return baseStyle
  }
}

// Add other helper functions (addWatermark, addTemplatedHeader, etc.)
const addWatermark = (pdf, level, pageWidth, pageHeight, primaryColor) => {
  pdf.setGState(new pdf.GState({ opacity: 0.1 }))
  pdf.setFont('helvetica', 'bold')
  pdf.setFontSize(48)
  pdf.setTextColor(primaryColor.r, primaryColor.g, primaryColor.b)
  
  const text = level.toUpperCase()
  const textWidth = pdf.getTextWidth(text)
  
  pdf.saveGraphicsState()
  pdf.text(text, (pageWidth - textWidth) / 2, pageHeight / 2, {
    angle: 45,
    align: 'center'
  })
  pdf.restoreGraphicsState()
  pdf.setGState(new pdf.GState({ opacity: 1.0 }))
}

const addTemplatedHeader = async (pdf, template, branding, yPosition, primaryColor, accentColor) => {
  switch (template) {
    case 'executive':
      return addExecutiveHeader(pdf, branding, yPosition, primaryColor, accentColor)
    case 'client':
      return addClientHeader(pdf, branding, yPosition, primaryColor, accentColor)
    case 'detailed':
      return addDetailedHeader(pdf, branding, yPosition, primaryColor)
    case 'modern':
      return addModernHeader(pdf, branding, yPosition, primaryColor, accentColor)
    case 'minimal':
      return addMinimalHeader(pdf, branding, yPosition, primaryColor)
    default:
      return addProfessionalHeader(pdf, branding, yPosition, primaryColor)
  }
}

const addProfessionalHeader = (pdf, branding, yPosition, primaryColor) => {
  const pageWidth = pdf.internal.pageSize.getWidth()
  
  // Company name on left, logo on right - no overlap
  pdf.setFontSize(16)
  pdf.setFont('helvetica', 'bold')
  pdf.setTextColor(primaryColor.r, primaryColor.g, primaryColor.b)
  pdf.text(branding.companyName || 'Professional Report', 20, yPosition + 8)
  
  // Logo positioned on the right side
  if (branding.logo) {
    try {
      pdf.addImage(branding.logo, 'PNG', pageWidth - 50, yPosition, 30, 18)
    } catch (error) {
      console.warn('Could not add logo:', error)
    }
  }
  
  // Compact contact info on one line
  pdf.setFontSize(8)
  pdf.setFont('helvetica', 'normal')
  pdf.setTextColor(100, 100, 100)
  
  const contacts = []
  if (branding.address) contacts.push(branding.address)
  if (branding.phone) contacts.push(`Tel: ${branding.phone}`)
  if (branding.email) contacts.push(`Email: ${branding.email}`)
  if (branding.website) contacts.push(`Web: ${branding.website}`)
  
  if (contacts.length > 0) {
    pdf.text(contacts.join(' | '), 20, yPosition + 16)
  }

  // Thin separator line
  pdf.setDrawColor(primaryColor.r, primaryColor.g, primaryColor.b)
  pdf.setLineWidth(0.5)
  pdf.line(20, yPosition + 22, pageWidth - 20, yPosition + 22)
  
  return yPosition + 28 // Reduced from 40
}

// Add other header templates...
const addExecutiveHeader = (pdf, branding, yPosition, primaryColor, accentColor) => {
  const pageWidth = pdf.internal.pageSize.getWidth()
  
  // Compact executive header
  pdf.setFillColor(primaryColor.r, primaryColor.g, primaryColor.b)
  pdf.rect(0, yPosition, pageWidth, 25, 'F') // Reduced from 35
  
  pdf.setTextColor(255, 255, 255)
  pdf.setFontSize(16) // Reduced from 20
  pdf.setFont('helvetica', 'bold')
  pdf.text(branding.companyName || 'Executive Report', 20, yPosition + 10)
  
  if (branding.logo) {
    try {
      pdf.addImage(branding.logo, 'PNG', pageWidth - 40, yPosition + 3, 25, 15) // Smaller
    } catch (error) {
      console.warn('Could not add logo:', error)
    }
  }
  
  pdf.setFontSize(8) // Reduced from 10
  pdf.text('CONFIDENTIAL ANALYSIS', 20, yPosition + 18)
  
  return yPosition + 30 // Reduced from 45
}

const addClientHeader = (pdf, branding, yPosition, primaryColor, accentColor) => {
  const pageWidth = pdf.internal.pageSize.getWidth()
  
  // Company name on left, logo on right
  pdf.setFontSize(14) // Reduced from 16
  pdf.setFont('helvetica', 'bold')
  pdf.setTextColor(primaryColor.r, primaryColor.g, primaryColor.b)
  pdf.text(branding.companyName || 'Client Report', 20, yPosition + 8)
  
  if (branding.logo) {
    try {
      pdf.addImage(branding.logo, 'PNG', pageWidth - 45, yPosition, 30, 18) // Smaller
    } catch (error) {
      console.warn('Could not add logo:', error)
    }
  }
  
  pdf.setFontSize(8) // Reduced from 10
  pdf.setFont('helvetica', 'normal')
  pdf.setTextColor(60, 60, 60)
  pdf.text('Professional Time Tracking & Project Management', 20, yPosition + 16)
  
  return yPosition + 25 // Reduced from 35
}

const addDetailedHeader = (pdf, branding, yPosition, primaryColor) => {
  const pageWidth = pdf.internal.pageSize.getWidth()
  
  // Compact header background
  pdf.setFillColor(248, 250, 252)
  pdf.rect(0, yPosition, pageWidth, 25, 'F') // Reduced from 40
  
  // Company name on left, logo on right
  pdf.setFontSize(14) // Reduced from 16
  pdf.setFont('helvetica', 'bold')
  pdf.setTextColor(primaryColor.r, primaryColor.g, primaryColor.b)
  pdf.text(branding.companyName || 'Detailed Analysis Report', 20, yPosition + 8)
  
  if (branding.logo) {
    try {
      pdf.addImage(branding.logo, 'PNG', pageWidth - 40, yPosition + 3, 25, 15) // Smaller logo
    } catch (error) {
      console.warn('Could not add logo:', error)
    }
  }
  
  // Compact contact info on one line
  pdf.setFontSize(7) // Reduced from 8
  pdf.setFont('helvetica', 'normal')
  pdf.setTextColor(100, 100, 100)
  
  const details = []
  if (branding.phone) details.push(`Tel: ${branding.phone}`)
  if (branding.email) details.push(`Email: ${branding.email}`)
  if (branding.website) details.push(`Web: ${branding.website}`)
  
  if (details.length > 0) {
    pdf.text(details.join(' | '), 20, yPosition + 16)
  }
  
  return yPosition + 30 // Reduced from 50
}

const addModernHeader = (pdf, branding, yPosition, primaryColor, accentColor) => {
  const pageWidth = pdf.internal.pageSize.getWidth()
  
  // Thin accent bar
  pdf.setFillColor(accentColor.r, accentColor.g, accentColor.b)
  pdf.rect(0, yPosition, pageWidth, 4, 'F') // Reduced from 8
  
  yPosition += 8
  
  // Company name on left, logo on right
  pdf.setFontSize(18) // Reduced from 24
  pdf.setFont('helvetica', 'bold')
  pdf.setTextColor(primaryColor.r, primaryColor.g, primaryColor.b)
  pdf.text(branding.companyName || 'Modern Report', 20, yPosition)
  
  if (branding.logo) {
    try {
      pdf.addImage(branding.logo, 'PNG', pageWidth - 40, yPosition - 6, 25, 15) // Smaller
    } catch (error) {
      console.warn('Could not add logo:', error)
    }
  }
  
  // Compact contact line
  pdf.setFontSize(7) // Reduced from 8
  pdf.setFont('helvetica', 'normal')
  pdf.setTextColor(120, 120, 120)
  
  const contacts = []
  if (branding.email) contacts.push(branding.email)
  if (branding.website) contacts.push(branding.website)
  if (branding.phone) contacts.push(branding.phone)
  
  if (contacts.length > 0) {
    pdf.text(contacts.join(' • '), 20, yPosition + 8)
  }
  
  return yPosition + 16 // Reduced from 20
}

const addMinimalHeader = (pdf, branding, yPosition, primaryColor) => {
  const pageWidth = pdf.internal.pageSize.getWidth()
  
  // Company name and logo on same line
  pdf.setFontSize(12) // Reduced from 14
  pdf.setFont('helvetica', 'normal')
  pdf.setTextColor(80, 80, 80)
  pdf.text(branding.companyName || 'Report', 20, yPosition)
  
  if (branding.logo) {
    try {
      pdf.addImage(branding.logo, 'PNG', pageWidth - 35, yPosition - 6, 20, 12)
    } catch (error) {
      console.warn('Could not add logo:', error)
    }
  }
  
  return yPosition + 15 // Reduced from 25
}

const addReportTitle = (pdf, reportType, filters, settings, yPosition, primaryColor) => {
  pdf.setFontSize(14) // Reduced from 18
  pdf.setFont('helvetica', 'bold')
  pdf.setTextColor(0, 0, 0)
  const reportTitle = getReportTitle(reportType)
  pdf.text(reportTitle, 20, yPosition)
  
  // Compact generation date on same line
  pdf.setFontSize(8) // Reduced from 9
  pdf.setTextColor(120, 120, 120)
  pdf.text(`Generated: ${new Date().toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'short', 
    day: 'numeric'
  })}`, pdf.internal.pageSize.getWidth() - 20, yPosition, { align: 'right' })
  
  return yPosition + 8 // Reduced from 15
}

const addDateRange = (pdf, dateRangeText, yPosition) => {
  pdf.setFontSize(9) // Reduced from 11
  pdf.setFont('helvetica', 'normal') // Changed from bold
  pdf.setTextColor(80, 80, 80)
  pdf.text(`Period: ${dateRangeText}`, 20, yPosition)
  
  return yPosition + 6 // Reduced from 15
}

const addFiltersSection = (pdf, filters, yPosition) => {
  const activeFilters = getActiveFilters(filters)
  if (activeFilters.length === 0) return yPosition
  
  // Compact inline filter display
  pdf.setFontSize(8)
  pdf.setFont('helvetica', 'normal')
  pdf.setTextColor(100, 100, 100)
  
  const filterText = `Filters: ${activeFilters.join(', ')}`
  const lines = pdf.splitTextToSize(filterText, pdf.internal.pageSize.getWidth() - 40)
  pdf.text(lines, 20, yPosition)
  
  return yPosition + (lines.length * 4) + 4 // Much more compact
}

const addExecutiveSummary = async (pdf, data, yPosition, primaryColor, accentColor) => {
  if (yPosition > 200) {
    pdf.addPage()
    yPosition = 20
  }
  
  pdf.setFillColor(accentColor.r, accentColor.g, accentColor.b, 0.1)
  pdf.rect(15, yPosition - 5, pdf.internal.pageSize.getWidth() - 30, 60, 'F')
  
  pdf.setFontSize(14)
  pdf.setFont('helvetica', 'bold')
  pdf.setTextColor(primaryColor.r, primaryColor.g, primaryColor.b)
  pdf.text('EXECUTIVE SUMMARY', 20, yPosition + 5)
  
  const summary = calculateSummaryWithSplits(data)
  
  pdf.setFontSize(10)
  pdf.setFont('helvetica', 'normal')
  pdf.setTextColor(60, 60, 60)
  
  const summaryText = [
    `Total Hours Tracked: ${summary.totalHours.toFixed(1)} hours`,
    `Billable Hours: ${summary.billableHours.toFixed(1)} hours (${summary.utilizationRate.toFixed(1)}% utilization)`,
    `Revenue Generated: $${summary.revenue.toLocaleString('en-US', { minimumFractionDigits: 2 })}`,
    `Average Rate: $${summary.billableHours > 0 ? (summary.revenue / summary.billableHours).toFixed(2) : '0.00'}/hour`
  ]
  
  let summaryY = yPosition + 15
  summaryText.forEach(line => {
    pdf.text(line, 20, summaryY)
    summaryY += 8
  })
  
  return yPosition + 70
}

const addCustomSection = (pdf, section, yPosition, primaryColor) => {
  if (yPosition > 250) {
    pdf.addPage()
    yPosition = 20
  }
  
  pdf.setFontSize(12)
  pdf.setFont('helvetica', 'bold')
  pdf.setTextColor(primaryColor.r, primaryColor.g, primaryColor.b)
  pdf.text(section.title || 'Custom Section', 20, yPosition)
  
  if (section.content) {
    pdf.setFontSize(10)
    pdf.setFont('helvetica', 'normal')
    pdf.setTextColor(60, 60, 60)
    const lines = pdf.splitTextToSize(section.content, 170)
    pdf.text(lines, 20, yPosition + 10)
    yPosition += 10 + (lines.length * 5)
  }
  
  return yPosition + 15
}

// FIXED: Add footer to all pages function
const addFooterToAllPages = (pdf, confidentiality, branding) => {
  const pageWidth = pdf.internal.pageSize.getWidth()
  const pageHeight = pdf.internal.pageSize.getHeight()
  const totalPages = pdf.internal.getNumberOfPages()
  
  // Loop through all pages and add footer
  for (let i = 1; i <= totalPages; i++) {
    pdf.setPage(i)
    addLegalFooter(pdf, confidentiality, branding, pageWidth, pageHeight)
  }
}

const addLegalFooter = (pdf, confidentiality, branding, pageWidth, pageHeight) => {
  const footerY = pageHeight - 20 // Moved up slightly
  
  // Confidentiality disclaimer (more compact)
  if (confidentiality?.enabled && confidentiality?.showDisclaimer) {
    pdf.setFontSize(6) // Reduced from 7
    pdf.setFont('helvetica', 'normal')
    pdf.setTextColor(140, 140, 140)
    
    const disclaimerText = 'CONFIDENTIAL: This document contains confidential information. Unauthorized disclosure is prohibited.'
    const lines = pdf.splitTextToSize(disclaimerText, pageWidth - 40)
    pdf.text(lines, 20, footerY - 8)
  }

  // Main footer info (single line, compact)
  pdf.setFontSize(6) // Reduced from 7
  pdf.setFont('helvetica', 'normal')
  pdf.setTextColor(120, 120, 120)
  
  // Left: Company copyright
  if (branding.companyName) {
    pdf.text(`© ${new Date().getFullYear()} ${branding.companyName}`, 20, footerY)
  }
  
  // Center: Page number
  pdf.text(`Page ${pdf.internal.getCurrentPageInfo().pageNumber} of ${pdf.internal.getNumberOfPages()}`, pageWidth / 2, footerY, { align: 'center' })
  
  // Right: Generation date (compact)
  const shortDate = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  pdf.text(`Generated: ${shortDate}`, pageWidth - 20, footerY, { align: 'right' })
}

const getReportTitle = (reportType) => {
  const titles = {
    summary: 'Executive Summary Report',
    detailed: 'Detailed Time Tracking Report',
    clients: 'Client Performance Analysis',
    projects: 'Project Analytics Report',
    users: 'Team Performance Report',
    roles: 'Role Analysis Report',
    timeline: 'Timeline Analysis Report'
  }
  return titles[reportType] || 'Time Tracking Report'
}

const getActiveFilters = (filters) => {
  const activeFilters = []
  
  if (filters.startDate) {
    const startDate = new Date(filters.startDate).toLocaleDateString()
    activeFilters.push(`Start Date: ${startDate}`)
  }
  if (filters.endDate) {
    const endDate = new Date(filters.endDate).toLocaleDateString()
    activeFilters.push(`End Date: ${endDate}`)
  }
  if (filters.clientId && filters.clientId !== 'all') {
    activeFilters.push(`Client: ${filters.clientName || 'Selected Client'}`)
  }
  if (filters.projectId && filters.projectId !== 'all') {
    activeFilters.push(`Project: ${filters.projectName || 'Selected Project'}`)
  }
  if (filters.userId && filters.userId !== 'all') {
    activeFilters.push(`User: ${filters.userName || 'Selected User'}`)
  }
  if (filters.status && filters.status !== 'all') {
    activeFilters.push(`Status: ${filters.status.charAt(0).toUpperCase() + filters.status.slice(1)}`)
  }
  if (filters.billableOnly) {
    activeFilters.push('Billable entries only')
  }
  
  return activeFilters
}

const generateFileName = (reportType, filters) => {
  const today = new Date().toISOString().split('T')[0]
  const reportName = getReportTitle(reportType)
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_]/g, '')
  
  let dateSuffix = today
  if (filters.startDate && filters.endDate) {
    dateSuffix = `${filters.startDate}_to_${filters.endDate}`
  } else if (filters.startDate) {
    dateSuffix = `from_${filters.startDate}`
  } else if (filters.endDate) {
    dateSuffix = `until_${filters.endDate}`
  }
  
  return `${reportName}_${dateSuffix}.pdf`
}

// Additional helper functions for enhanced content rendering

const addMetricsCards = (pdf, data, yPosition, template, primaryColor, accentColor) => {
  const totalHours = data.reduce((sum, entry) => sum + (entry.duration_hours || 0), 0)
  const billableHours = data.reduce((sum, entry) => sum + (entry.is_billable ? (entry.duration_hours || 0) : 0), 0)
  const revenue = data.reduce((sum, entry) => sum + (entry.is_billable ? (entry.duration_hours || 0) * (entry.hourly_rate || 0) : 0), 0)
  const avgRate = billableHours > 0 ? revenue / billableHours : 0

  const cardWidth = 40
  const cardHeight = 25
  const spacing = 5
  const startX = 20

  // Card data
  const cards = [
    { title: 'Total Hours', value: `${totalHours.toFixed(1)}h`, color: primaryColor },
    { title: 'Billable Hours', value: `${billableHours.toFixed(1)}h`, color: accentColor },
    { title: 'Revenue', value: `$${revenue.toLocaleString()}`, color: primaryColor },
    { title: 'Avg Rate', value: `$${avgRate.toFixed(2)}/hr`, color: accentColor }
  ]

  cards.forEach((card, index) => {
    const x = startX + (index * (cardWidth + spacing))
    
    // Card background
    pdf.setFillColor(card.color.r, card.color.g, card.color.b, 0.1)
    pdf.rect(x, yPosition, cardWidth, cardHeight, 'F')
    
    // Card border
    pdf.setDrawColor(card.color.r, card.color.g, card.color.b)
    pdf.setLineWidth(0.5)
    pdf.rect(x, yPosition, cardWidth, cardHeight)
    
    // Title
    pdf.setFontSize(8)
    pdf.setFont('helvetica', 'normal')
    pdf.setTextColor(100, 100, 100)
    pdf.text(card.title, x + 2, yPosition + 6)
    
    // Value
    pdf.setFontSize(12)
    pdf.setFont('helvetica', 'bold')
    pdf.setTextColor(card.color.r, card.color.g, card.color.b)
    pdf.text(card.value, x + 2, yPosition + 16)
  })

  return yPosition + cardHeight + 15
}

const addChartPlaceholder = (pdf, title, yPosition, width = 170, height = 60) => {
  const startX = 20
  
  // Chart background
  pdf.setFillColor(248, 250, 252)
  pdf.rect(startX, yPosition, width, height, 'F')
  
  // Chart border
  pdf.setDrawColor(200, 200, 200)
  pdf.setLineWidth(0.5)
  pdf.rect(startX, yPosition, width, height)
  
  // Chart title
  pdf.setFontSize(10)
  pdf.setFont('helvetica', 'bold')
  pdf.setTextColor(60, 60, 60)
  pdf.text(title, startX + 5, yPosition + 10)
  
  // Placeholder text
  pdf.setFontSize(8)
  pdf.setFont('helvetica', 'normal')
  pdf.setTextColor(120, 120, 120)
  pdf.text('[Chart visualization would appear here]', startX + 5, yPosition + height/2)
  
  return yPosition + height + 10
}

const addSectionDivider = (pdf, title, yPosition, primaryColor) => {
  // Check for page break
  if (yPosition > 250) {
    pdf.addPage()
    yPosition = 20
  }
  
  // Section line
  pdf.setDrawColor(primaryColor.r, primaryColor.g, primaryColor.b)
  pdf.setLineWidth(1)
  pdf.line(20, yPosition, pdf.internal.pageSize.getWidth() - 20, yPosition)
  
  // Section title
  pdf.setFontSize(12)
  pdf.setFont('helvetica', 'bold')
  pdf.setTextColor(primaryColor.r, primaryColor.g, primaryColor.b)
  pdf.text(title, 20, yPosition + 10)
  
  return yPosition + 20
}

const addInsights = (pdf, data, yPosition, primaryColor) => {
  // Generate insights based on data
  const insights = generateInsights(data)
  
  if (insights.length === 0) return yPosition
  
  yPosition = addSectionDivider(pdf, 'Key Insights', yPosition, primaryColor)
  
  pdf.setFontSize(9)
  pdf.setFont('helvetica', 'normal')
  pdf.setTextColor(60, 60, 60)
  
  insights.forEach((insight, index) => {
    pdf.text(`• ${insight}`, 25, yPosition)
    yPosition += 8
  })
  
  return yPosition + 10
}

const generateInsights = (data) => {
  const insights = []
  
  if (!data || data.length === 0) return insights
  
  const totalHours = data.reduce((sum, entry) => sum + (entry.duration_hours || 0), 0)
  const billableHours = data.reduce((sum, entry) => sum + (entry.is_billable ? (entry.duration_hours || 0) : 0), 0)
  const utilization = totalHours > 0 ? (billableHours / totalHours * 100) : 0
  
  // Utilization insights
  if (utilization > 85) {
    insights.push('Excellent utilization rate indicates highly efficient time allocation')
  } else if (utilization > 70) {
    insights.push('Good utilization rate with room for minor optimization')
  } else if (utilization > 50) {
    insights.push('Moderate utilization - consider analyzing non-billable time allocation')
  } else {
    insights.push('Low utilization rate - review time tracking and project allocation')
  }
  
  // Project diversity
  const uniqueProjects = [...new Set(data.map(entry => entry.project_name).filter(Boolean))].length
  if (uniqueProjects > 5) {
    insights.push(`High project diversity with ${uniqueProjects} active projects`)
  } else if (uniqueProjects > 1) {
    insights.push(`Working across ${uniqueProjects} projects shows balanced portfolio`)
  } else {
    insights.push('Focused on single project - consider diversification opportunities')
  }
  
  // Revenue insights
  const revenue = data.reduce((sum, entry) => sum + (entry.is_billable ? (entry.duration_hours || 0) * (entry.hourly_rate || 0) : 0), 0)
  const avgRate = billableHours > 0 ? revenue / billableHours : 0
  
  if (avgRate > 150) {
    insights.push('Premium rate positioning indicates high-value service delivery')
  } else if (avgRate > 75) {
    insights.push('Competitive rate structure aligned with market standards')
  } else if (avgRate > 0) {
    insights.push('Consider rate optimization to improve revenue per hour')
  }
  
  return insights.slice(0, 3) // Limit to top 3 insights
}